import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { localDateStr } from '../utils/dates';
import { useUrlParams } from '../hooks/useUrlState';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/FeedbackContext';
import MultiSelectFilter, { listValue, listParam } from './common/MultiSelectFilter';
import EntityLink from './EntityLink';
import StateBlock from './StateBlock';
import Badge from './Badge';
import Pagination from './Pagination';

/**
 * AP Payments → "Payments made" (2026-10-22): every AP payment in a date
 * range with its payment-advice status, so recent payments can be sent a
 * payment advice. Advice status comes from the server (utils/adviceStatus.js,
 * which calls the Vendor Remittances readiness rule). All PDF / ZIP /
 * mark-as-sent work stays on Vendor Remittances; "Verify from bank" is an
 * admin bulk action with a preview, never automatic.
 */

const money = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const utcStamp = s => (s ? `${s.slice(0, 16)} UTC` : '');

const ADVICE = {
  not_sent: { label: 'Not sent' },
  sent: { label: 'Sent' },
  not_ready: { label: 'Not ready' },
};
const VERIFIED = {
  verified: { label: 'Verified', test: r => r.verification_status === 'verified' },
  unverified: { label: 'Not verified', test: r => r.verification_status !== 'verified' },
};

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localDateStr(d);
}
const PRESETS = [
  ['Today', () => [localDateStr(), localDateStr()]],
  ['Last 3 days', () => [daysAgo(2), localDateStr()]],
  ['Last 7 days', () => [daysAgo(6), localDateStr()]],
  ['This month', () => { const d = new Date(); return [localDateStr(new Date(d.getFullYear(), d.getMonth(), 1)), localDateStr()]; }],
];

function AdviceCell({ r }) {
  if (r.advice === 'sent') {
    return <><Badge variant="success">Sent</Badge><div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{utcStamp(r.sent_at)}{r.sent_by ? ` by ${r.sent_by}` : ''}</div></>;
  }
  if (r.advice === 'not_ready') {
    const why = r.advice_reason || '';
    return <><Badge variant="warning">Not ready</Badge><div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, maxWidth: 260 }} title={why}>{why.length > 120 ? why.slice(0, 117) + '…' : why}</div></>;
  }
  return <Badge>Not sent</Badge>;
}

export default function PaymentsMadeTab() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { get, set } = useUrlParams();
  const [defFrom, defTo] = PRESETS[2][1]();
  const from = get('from', defFrom);
  const to = get('to', defTo);
  const vendorFilter = listValue(get('mvendor', ''));
  const adviceFilter = listValue(get('advice', '')).filter(k => ADVICE[k]);
  const verifiedFilter = listValue(get('verified', '')).filter(k => VERIFIED[k]);
  const search = get('msearch', '');
  const page = get('mpage', 1);
  const pageSize = get('mpageSize', 50);

  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [verify, setVerify] = useState(null); // { loading, pass, fail, error }
  const [confirming, setConfirming] = useState(false);

  function load() {
    setError('');
    api.get('/remittances/payments-made', { params: { from, to } })
      .then(r => setRows(r.data.rows)).catch(e => setError(e.response?.data?.error || 'Could not load payments'));
  }
  useEffect(() => { setRows(null); setSelected(new Set()); load(); }, [from, to]);

  const vendorOptions = useMemo(() => {
    const m = new Map();
    for (const r of rows || []) if (!m.has(r.payee_key)) m.set(r.payee_key, r.payee_name);
    return [...m].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const filtered = useMemo(() => (rows || []).filter(r => {
    if (vendorFilter.length && !vendorFilter.includes(r.payee_key)) return false;
    if (adviceFilter.length && !adviceFilter.includes(r.advice)) return false;
    if (verifiedFilter.length && !verifiedFilter.some(k => VERIFIED[k].test(r))) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return [r.payee_name, r.contractor_name, r.invoice_number, r.trace_id].some(x => (x || '').toLowerCase().includes(s));
  }), [rows, vendorFilter.join(), adviceFilter.join(), verifiedFilter.join(), search]);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Ticked rows still in view; otherwise every filtered row.
  const ticked = filtered.filter(r => selected.has(r.payment_id));
  const target = ticked.length ? ticked : filtered;
  const allSelected = filtered.length > 0 && filtered.every(r => selected.has(r.payment_id));
  const toggle = id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(s => { const n = new Set(s); filtered.forEach(r => (allSelected ? n.delete(r.payment_id) : n.add(r.payment_id))); return n; });

  const counts = useMemo(() => {
    const c = { not_sent: 0, sent: 0, not_ready: 0 };
    for (const r of rows || []) c[r.advice]++;
    return c;
  }, [rows]);

  function sendAdvice() {
    const payees = [...new Set(target.map(r => r.payee_key))];
    const q = new URLSearchParams({ from, to, unsent: '1' });
    if (payees.length) q.set('payees', payees.join(','));
    navigate(`/vendor-remittances?${q.toString()}`);
  }

  async function previewVerify() {
    const ids = target.filter(r => r.verification_status !== 'verified').map(r => r.payment_id);
    if (!ids.length) { toast('No unverified payments in the ' + (ticked.length ? 'ticked' : 'filtered') + ' rows', 'info'); return; }
    setVerify({ loading: true, pass: [], fail: [] });
    try {
      const r = await api.post('/ap-invoices/payments-made/verify-from-bank/preview', { ids });
      setVerify({ loading: false, ...r.data });
    } catch (e) {
      setVerify({ loading: false, pass: [], fail: [], error: e.response?.data?.error || 'Preview failed' });
    }
  }
  async function confirmVerify() {
    setConfirming(true);
    try {
      const r = await api.post('/ap-invoices/payments-made/verify-from-bank/confirm', { ids: verify.pass.map(p => p.payment_id) });
      toast(`${r.data.verified} payment(s) verified against PNC (${money(r.data.total)})` + (r.data.fail.length ? `; ${r.data.fail.length} no longer passed and were left unverified` : ''), r.data.fail.length ? 'info' : 'success');
      setVerify(null);
      load();
    } catch (e) {
      toast(e.response?.data?.error || 'Verify failed', 'error');
    } finally { setConfirming(false); }
  }

  const hasFilters = vendorFilter.length || adviceFilter.length || verifiedFilter.length || search;

  return (
    <>
      <div className="filter-bar">
        <label style={{ fontSize: 12, color: 'var(--muted)' }}>Paid from</label>
        <input type="date" value={from} onChange={e => set({ from: e.target.value, to: e.target.value > to ? e.target.value : to, mpage: '' })} />
        <label style={{ fontSize: 12, color: 'var(--muted)' }}>to</label>
        <input type="date" value={to} min={from} onChange={e => set({ to: e.target.value, mpage: '' })} />
        {PRESETS.map(([label, fn]) => {
          const [f, t] = fn();
          return <button key={label} type="button" className={'btn btn-sm ' + (f === from && t === to ? 'btn-primary' : 'btn-ghost')} onClick={() => set({ from: f, to: t, mpage: '' })}>{label}</button>;
        })}
      </div>
      <div className="filter-bar">
        <input type="text" className="filter-search" placeholder="Search payee, contractor, invoice #, trace…"
          value={search} onChange={e => set({ msearch: e.target.value, mpage: '' })} />
        <MultiSelectFilter label="Vendor" options={vendorOptions} selected={vendorFilter} onChange={v => set({ mvendor: listParam(v), mpage: '' })} />
        <MultiSelectFilter label="Advice" options={Object.entries(ADVICE).map(([value, a]) => ({ value, label: a.label, count: counts[value] }))} selected={adviceFilter} onChange={v => set({ advice: listParam(v), mpage: '' })} />
        <MultiSelectFilter label="Verified" options={Object.entries(VERIFIED).map(([value, a]) => ({ value, label: a.label, count: (rows || []).filter(a.test).length }))} selected={verifiedFilter} onChange={v => set({ verified: listParam(v), mpage: '' })} />
        {hasFilters ? <button className="btn btn-ghost btn-sm" onClick={() => set({ mvendor: '', advice: '', verified: '', msearch: '', mpage: '' })}>Clear filters</button> : null}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {isAdmin && (
            <button className="btn btn-secondary btn-sm" onClick={previewVerify} disabled={!rows || verify?.loading}
              title="Check the unverified payments in the ticked (or filtered) rows against the imported PNC payment reports, then confirm">
              ✓ Verify from bank{ticked.length ? ` (${ticked.length} ticked)` : ''}
            </button>
          )}
          <button className="btn btn-primary btn-sm" onClick={sendAdvice} disabled={!target.length}
            title="Open Vendor Remittances for this date range, with these rows' vendors and 'Not yet sent' selected">
            Send payment advice…
          </button>
        </div>
      </div>

      {verify && (
        <>
          <div className="panel-overlay" onClick={() => !confirming && setVerify(null)} />
          <div className="quick-panel" style={{ width: 760, maxWidth: '100vw' }}>
            <div className="drawer-header">
              <h3 className="drawer-title">Verify from bank</h3>
              <button className="drawer-close" onClick={() => !confirming && setVerify(null)}>×</button>
            </div>
            <div className="panel-body" style={{ fontSize: 13 }}>
              <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 12 }}>
                A payment passes only if its trace ID is a <b>Processed</b> payment in the imported PNC payment reports, PARAM's cash on that
                trace equals the bank amount, and the payment date equals the bank date. Nothing is verified until you confirm.
              </div>
              {verify.loading && <StateBlock type="loading" />}
              {verify.error && <div style={{ color: 'var(--error)', fontSize: 12 }}>{verify.error}</div>}
              {!verify.loading && !verify.error && (
                <>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Will be verified: {verify.pass.length} ({money(verify.pass.reduce((s, p) => s + p.cash, 0))})</div>
                  {verify.pass.length > 0 && (
                    <table className="data-table" style={{ fontSize: 12, marginBottom: 12 }}>
                      <thead><tr><th>Date</th><th>Payee</th><th>Invoice</th><th>Trace</th><th style={{ textAlign: 'right' }}>Cash</th><th style={{ textAlign: 'right' }}>Bank transfer</th></tr></thead>
                      <tbody>{verify.pass.map(p => (
                        <tr key={p.payment_id}><td>{p.payment_date}</td><td>{p.payee_name}</td><td>{p.invoice_number}</td><td>{p.trace_id}</td>
                          <td style={{ textAlign: 'right' }}>{money(p.cash)}</td><td style={{ textAlign: 'right' }}>{money(p.bank_amount)}</td></tr>
                      ))}</tbody>
                    </table>
                  )}
                  {verify.fail.length > 0 && (
                    <>
                      <div style={{ fontWeight: 600, color: 'var(--warning)', marginBottom: 6 }}>Not verified - left as they are: {verify.fail.length}</div>
                      <table className="data-table" style={{ fontSize: 12, marginBottom: 12 }}>
                        <thead><tr><th>Date</th><th>Payee</th><th>Invoice</th><th>Trace</th><th style={{ textAlign: 'right' }}>Cash</th><th>Reason</th></tr></thead>
                        <tbody>{verify.fail.map(p => (
                          <tr key={p.payment_id}><td>{p.payment_date}</td><td>{p.payee_name}</td><td>{p.invoice_number}</td><td>{p.trace_id || '—'}</td>
                            <td style={{ textAlign: 'right' }}>{money(p.cash)}</td><td style={{ color: 'var(--warning)' }}>{p.reason}</td></tr>
                        ))}</tbody>
                      </table>
                    </>
                  )}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button className="btn btn-secondary" onClick={() => setVerify(null)} disabled={confirming}>Cancel</button>
                    <button className="btn btn-primary" onClick={confirmVerify} disabled={confirming || !verify.pass.length}>
                      {confirming ? 'Verifying…' : `Verify ${verify.pass.length} payment(s)`}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {error && <StateBlock type="error" message={error} action={<button className="btn btn-primary" onClick={load}>Try again</button>} />}
      {!error && rows === null && <StateBlock type="loading" />}
      {!error && rows !== null && filtered.length === 0 && (
        <StateBlock type="empty" title="No payments" message={`No AP payments ${from === to ? 'on ' + from : `between ${from} and ${to}`}${hasFilters ? ' match the filters' : ''}.`} />
      )}

      {!error && filtered.length > 0 && (
        <>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
            {filtered.length} payment(s) · {money(filtered.reduce((s, r) => s + r.cash, 0))} cash ·
            {' '}{filtered.filter(r => r.advice === 'not_sent').length} not sent, {filtered.filter(r => r.advice === 'sent').length} sent, {filtered.filter(r => r.advice === 'not_ready').length} not ready
            {ticked.length > 0 && <> · <strong style={{ color: 'var(--text)' }}>{ticked.length} ticked</strong></>}
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 28 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} title="Tick / untick every row matching the filters" /></th>
                <th>Paid</th><th>Payee</th><th>Contractor</th><th>AP Invoice #</th>
                <th style={{ textAlign: 'right' }}>Cash</th><th style={{ textAlign: 'right' }}>EPD / BGV</th><th>Trace ID</th><th>Verified</th><th>Advice</th>
              </tr>
            </thead>
            <tbody>
              {paged.map(r => (
                <tr key={r.payment_id}>
                  <td><input type="checkbox" checked={selected.has(r.payment_id)} onChange={() => toggle(r.payment_id)} /></td>
                  <td style={{ whiteSpace: 'nowrap' }}>{r.payment_date}</td>
                  <td>{r.vendor_id ? <EntityLink type="vendor" id={r.vendor_id}>{r.payee_name}</EntityLink> : <EntityLink type="contractor" id={r.contractor_id}>{r.payee_name}</EntityLink>}</td>
                  <td>{r.contractor_id ? <EntityLink type="contractor" id={r.contractor_id}>{r.contractor_name}</EntityLink> : '—'}</td>
                  <td><EntityLink type="ap_invoice" id={r.ap_invoice_id} style={{ fontWeight: 600 }}>{r.invoice_number}</EntityLink></td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{money(r.cash)}</td>
                  <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--muted)' }}>{r.epd || r.bgv ? `${money(r.epd)} / ${money(r.bgv)}` : '—'}</td>
                  <td style={{ fontSize: 12 }}>{r.trace_id || '—'}</td>
                  <td>{r.verification_status === 'verified' ? <Badge variant="success">Verified</Badge> : <Badge>{r.verification_status}</Badge>}</td>
                  <td><AdviceCell r={r} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} pageSize={pageSize} total={filtered.length}
            onPageChange={p => set({ mpage: p })}
            onPageSizeChange={s => set({ mpageSize: s, mpage: '' })} />
        </>
      )}
    </>
  );
}
